import React, { useRef, useState } from 'react';
import './Gallery.css';
import PageHero from './PageHero';
import { camps } from '../data/camps';
import { Camp } from '../types';
import { Page } from './Navigation';
import { MIN_BOOKING_NIGHTS } from '../constants/booking';


interface GalleryProps {
  onNavigate?: (page: Page, campId?: string) => void;
}

const Gallery: React.FC<GalleryProps> = ({ onNavigate }) => {
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const selectedImages = selectedCamp?.images ?? [];
  const hasModalImages = selectedImages.length > 0;
  const safeModalIndex = hasModalImages
    ? ((modalImageIndex % selectedImages.length) + selectedImages.length) % selectedImages.length
    : 0;

  return (
    <div className="gallery-page">
      <PageHero 
        title="Book Your Stay"
        subtitle="Experience the Abrolhos Islands"
      />
      
      <div className="gallery-container">
        <section className="content-section">
          <div className="content-container">
            <div className="section-header">
              <h2>BEFORE YOU BOOK</h2>
              <div className="section-divider"></div>
              <p className="section-tagline">This is a remote island property.</p>
              <div className="before-booking-items">
                <p className="before-booking-item">🚤 Guests must arrange their own transport to and from the Abrolhos Islands</p>
                <p className="before-booking-item">🏝 We are on-site as caretakers during guest stays</p>
                <p className="before-booking-item">🛎 This is not a serviced hotel (no reception desk, no room service)</p>
                <p className="before-booking-item">🛒 Supplies are limited — plan accordingly</p>
                <p className="before-booking-item">📅 Minimum stay {MIN_BOOKING_NIGHTS} nights</p>
                <p className="before-booking-item">🌊 Weather may affect travel</p>
              </div>
            </div>
          </div>
        </section>

        <div className="camps-gallery">
          {camps.map((camp) => (
            <div key={camp.id} className="gallery-camp-card">
              <div className="camp-hero-image">
                {camp.images && camp.images.length > 0 ? (
                  <img src={camp.images[0]} alt={camp.name} />
                ) : (
                  <div className="camp-image-placeholder">
                    <div className="placeholder-content">
                      <span className="placeholder-icon">🏕️</span>
                      <p>Camp Photo</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="camp-card-content">
                <div className="camp-card-header">
                  <h2>{camp.name}</h2>
                  <span className="camp-price-badge">${camp.pricePerNight}/night</span>
                </div>
                <p className="camp-location-badge">📍 {camp.location}</p>
                <p className="camp-description">{camp.description}</p>
                
                <div className="camp-specs">
                  <div className="spec-item">
                    <span className="spec-icon">👥</span>
                    <span>Up to {camp.maxGuests} guests</span>
                  </div>
                </div>

                <div className="camp-amenities-preview">
                  <strong>Amenities:</strong>
                  <div className="amenities-tags">
                    {camp.amenities.map((amenity, idx) => (
                      <span key={idx} className="amenity-tag">{amenity}</span>
                    ))}
                  </div>
                </div>

                <button 
                  className="view-details-btn"
                  onClick={() => {
                    setSelectedCamp(camp);
                    setModalImageIndex(0);
                  }}
                >
                  View Full Details
                </button>
              </div>
            </div>
          ))}
        </div>

        {selectedCamp && (
          <div className="camp-modal-overlay" onClick={() => setSelectedCamp(null)}>
            <div className="camp-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedCamp(null)}>
                ×
              </button>
              {hasModalImages ? (
                <div
                  className="modal-image-hero"
                  onTouchStart={(e) => {
                    touchStartX.current = e.touches[0]?.clientX ?? null;
                  }}
                  onTouchEnd={(e) => {
                    const startX = touchStartX.current;
                    const endX = e.changedTouches[0]?.clientX ?? null;
                    touchStartX.current = null;

                    if (startX == null || endX == null) return;
                    const delta = endX - startX;
                    const threshold = 40; // px

                    if (Math.abs(delta) < threshold) return;
                    if (delta < 0) setModalImageIndex((i) => i + 1); // swipe left -> next
                    if (delta > 0) setModalImageIndex((i) => i - 1); // swipe right -> prev
                  }}
                >
                  <img src={selectedImages[safeModalIndex]} alt={selectedCamp.name} />
                  {selectedImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="modal-nav modal-nav-prev"
                        onClick={() => setModalImageIndex((i) => i - 1)}
                        aria-label="Previous photo"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="modal-nav modal-nav-next"
                        onClick={() => setModalImageIndex((i) => i + 1)}
                        aria-label="Next photo"
                      >
                        ›
                      </button>
                      <div className="modal-image-counter">
                        {safeModalIndex + 1} / {selectedImages.length}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="modal-image-placeholder">
                  <span className="placeholder-icon-large">🏕️</span>
                </div>
              )}
              <div className="modal-content">
                <h2>{selectedCamp.name}</h2>
                <p className="modal-location">📍 {selectedCamp.location}</p>
                <p className="modal-price">${selectedCamp.pricePerNight} per night</p>

                {hasModalImages && selectedImages.length > 1 && (
                  <div className="modal-thumbs" aria-label="Photo thumbnails">
                    {selectedImages.map((src, idx) => (
                      <button
                        key={`${src}-${idx}`}
                        type="button"
                        className={`modal-thumb ${idx === safeModalIndex ? 'active' : ''}`}
                        onClick={() => setModalImageIndex(idx)}
                        aria-label={`View photo ${idx + 1}`}
                      >
                        <img src={src} alt="" />
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="modal-section">
                  <h3>Description</h3>
                  <p>{selectedCamp.description}</p>
                </div>

                <div className="modal-section">
                  <h3>Capacity</h3>
                  <p>Up to {selectedCamp.maxGuests} guests</p>
                </div>

                <div className="modal-section">
                  <h3>Amenities</h3>
                  <ul className="amenities-list">
                    {selectedCamp.amenities.map((amenity, idx) => (
                      <li key={idx}>
                        <span className="check-icon">✓</span>
                        {amenity}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="modal-actions">
                  <button 
                    className="book-now-btn"
                    onClick={() => {
                      setSelectedCamp(null);
                      if (onNavigate) {
                        onNavigate('booking', selectedCamp.id);
                      }
                    }}
                  >
                    Book This Camp
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Gallery;

